// #region imports

// --- Angular
import { Injectable } from '@angular/core';

// --- RxJs
import { BehaviorSubject, Observable, of, Subject, Subscription } from 'rxjs';
import { map } from 'rxjs/operators';

// --- HS Code
import { Claims } from '../account/claims';
import { Constants, RegistrationCodeValidationResultModel, RegistrationCodeValidationResults, RegistrationResultModel } from '../../../models';
import { EnvService } from '../env.service';
import { OidcService } from '../account/oidc.service';

// #endregion

@Injectable({
  providedIn: 'root'
})
export abstract class SelfRegistrationBaseService {

  protected _validationResult$: BehaviorSubject<RegistrationCodeValidationResultModel>;
  get validationResult$(): Observable<RegistrationCodeValidationResultModel> {
    return this._validationResult$.asObservable();
  }

  protected _registrationResult$: Subject<RegistrationResultModel>;
  get registrationResult$(): Observable<RegistrationResultModel> {
    return this._registrationResult$.asObservable();
  }

  protected _registrationCode$: Subject<string>;
  get registrationCode$(): Observable<string> {
    return this._registrationCode$.asObservable();
  }

  protected _openSelfRegDlg$: Subject<void> = new Subject<void>;
  get openSelfRegDlg(): Observable<void> {
    return this._openSelfRegDlg$;
  }

  public get isSelfRegistrationEnabled$(): Observable<boolean> {
    if (this._envService.selfRegistration.toLowerCase() === Constants.FEATURE_OFF) {
      return of(false);
    }

    return this._oidcService.claims$.pipe(
      map((claims: Claims) => {

        if (this._envService.selfRegistration.toLocaleLowerCase() !== Constants.FEATURE_ON) {
          const claimName = this._envService.selfRegistration.toLowerCase();

          if (!claims.hasClaim(claimName) || !claims.is(claimName)) {
            return false;
          }
        }

        if (!claims.hasClaim(Constants.CLAIMS_COMPANY_ID)) {
          return false;
        }

        const companyId = claims.getClaimValue(Constants.CLAIMS_COMPANY_ID);
        if (companyId.toUpperCase().indexOf(Constants.CLAIMS_COMPANY_BENCH) < 0) {
          return false;
        }

        return true;
      })
    );
  }

  constructor(
    private _envService: EnvService,
    private _oidcService: OidcService
  ) {
    this._registrationCode$ = new Subject<string>();
    this._validationResult$ = new BehaviorSubject<RegistrationCodeValidationResultModel>({ validationResult: RegistrationCodeValidationResults.unvalidated, registrationCode: null, participantId: null });
    this._registrationResult$ = new Subject<RegistrationResultModel>();
  }

  public clearRegistration(): void {
    this._registrationCode$ = new Subject<string>();
    this._validationResult$ = new BehaviorSubject<RegistrationCodeValidationResultModel>({ validationResult: RegistrationCodeValidationResults.unvalidated, registrationCode: null, participantId: null });
    this._registrationResult$ = new Subject<RegistrationResultModel>();
  }

  public openSelfRegistration(): void {
    this._openSelfRegDlg$.next();
  }

  public clearValidationResult(): void {
    this._validationResult$.next({ validationResult: RegistrationCodeValidationResults.unvalidated, registrationCode: null, participantId: null });
  }

  public abstract validateRegistrationCode(registrationCode: string): void;
  public abstract register(registrationCode: string): void;
}
